import React, { FC, useState, useMemo } from 'react';

import { Button, Table } from 'antd';
import { observer } from 'mobx-react';

import i18n from '@/content';
import RecipientRoleSelect, { SharedItemType } from '../../../Common/RecipientRoleSelect';
import RecipientCell from './RecipientCell';
import RevocationWarningModal from '../RevocationWarningModal';
import { checkPolicyForwardingStatus } from '../RevocationWarningModal/helpers';
import { useStores } from '../../../hooks';
import {
    checkRequiresPhoneVerification,
    checkRequiresPasswordVerification,
    checkRequiresPasswordProtection,
} from '../../../utils';
import {
    PhoneIdentity, Policy, RecipientRoles, RecipientType, SecuredFile, SharedUser,
} from '@/types/types';
import { SharedUserToDisplay } from '../../../../stores';
import styles from './ShareSettingsTable.module.scss';

const { Column } = Table;

const nameSpace = 'filesTable.modals.settings.shareSettings';

const createOwnerUser = (email: string, userId: string): SharedUserToDisplay => ({
    email,
    userId,
    recipientRole: 'coOwner',
    recipientType: 'user',
    deleted: false,
});

interface ShareSettingsProps {
    fileSelected: SecuredFile;
    sharedItemType?: SharedItemType;
    sharedUsers: SharedUserToDisplay[];
    onChangeUsers: (newUsers: SharedUserToDisplay[]) => void;
    onNavigateToPolicy?: () => void;
    selectedPolicy?: Policy;
}

const ShareSettingsTable: FC<ShareSettingsProps> = observer(({
    fileSelected,
    sharedItemType,
    onChangeUsers,
    sharedUsers,
    onNavigateToPolicy,
    selectedPolicy,
}) => {
    const [recipientForPhoneEditing, setRecipientForPhoneEditing] = useState<string>('');
    const [warningModalState, setWarningModalState] = useState<{
        isOpen: boolean;
        userEmail: string;
        recipientType: RecipientType;
    }>({ isOpen: false, userEmail: '', recipientType: 'user' });

    const {
        filesListStore: { isMyFiles },
        policyStore: { policyList },
        usersPhonesStore: {
            usersPhonesMap,
            saveRecipientPhone,
        },
    } = useStores();

    // Use selectedPolicy if provided, otherwise fall back to file's policy
    const currentPolicy = useMemo(() => {
        if (selectedPolicy) {
            return selectedPolicy;
        }
        return policyList.find((p) => p.id === fileSelected.policy_id);
    }, [selectedPolicy, policyList, fileSelected.policy_id]);

    // Check if the current policy has forwarding enabled
    const forwardingStatus = useMemo(() => {
        return checkPolicyForwardingStatus(currentPolicy);
    }, [currentPolicy]);

    // Check policy requirements for phone and password
    const policyRequirements = useMemo(() => {
        if (!currentPolicy) {
            return { requiresPhone: false, requiresPassword: false };
        }
        return {
            requiresPhone: checkRequiresPhoneVerification(currentPolicy.settings),
            requiresPassword: checkRequiresPasswordVerification(currentPolicy.settings)
                || checkRequiresPasswordProtection(currentPolicy.settings),
        };
    }, [currentPolicy]);

    const usersToDisplay: SharedUserToDisplay[] = [
        createOwnerUser(fileSelected.owner_email, fileSelected.owner_id),
        ...sharedUsers,
    ];

    const changeRole = (email: string, recipientRole: RecipientRoles): void => {
        const newUsersList = sharedUsers.map((user) => {
            if (user.email === email) {
                return { ...user, recipientRole };
            }
            return user;
        });
        onChangeUsers(newUsersList);
    };

    const toggleUserAccess = (email: string, isDeleted: boolean): void => {
        const newUsersList = sharedUsers.map((user) => {
            if (user.email === email) {
                return { ...user, deleted: !isDeleted };
            }
            return user;
        });
        onChangeUsers(newUsersList);
    };

    const handleRevokeClick = (email: string, isDeleted: boolean, recipientType: RecipientType): void => {
        if (isDeleted || !forwardingStatus.shouldShowWarning) {
            toggleUserAccess(email, isDeleted);
            return;
        }

        setWarningModalState({ isOpen: true, userEmail: email, recipientType });
    };

    const handleWarningContinue = (): void => {
        toggleUserAccess(warningModalState.userEmail, false);
        setWarningModalState({ isOpen: false, userEmail: '', recipientType: 'user' });
    };

    const handleWarningChangePolicy = (): void => {
        setWarningModalState({ isOpen: false, userEmail: '', recipientType: 'user' });
        onNavigateToPolicy?.();
    };

    const handleRevokeAllDomain = (): void => {
        const userDomain = warningModalState.userEmail.split('@')[1]?.toLowerCase();
        if (!userDomain) return;

        const newUsersList = sharedUsers.map((user) => {
            const domain = user.email.split('@')[1]?.toLowerCase();
            return domain === userDomain ? { ...user, deleted: true } : user;
        });

        onChangeUsers(newUsersList);
        setWarningModalState({ isOpen: false, userEmail: '', recipientType: 'user' });
    };

    const handleWarningCancel = (): void => {
        setWarningModalState({ isOpen: false, userEmail: '', recipientType: 'user' });
    };

    // TODO: check if it works in all cases. If not, use alphanumeric_email check from BE download-service
    const checkIsElementEnabled = (userId: string): boolean => (
        (userId !== fileSelected.owner_id)
        && (isMyFiles || fileSelected?.menu?.edit_shares)
    );

    const setPhone = (email: string, phone: PhoneIdentity): void => {
        saveRecipientPhone(email, phone);
        setRecipientForPhoneEditing('');
    };

    return (
        <>
            <Table
                rowKey="userId"
                size="middle"
                pagination={false}
                dataSource={usersToDisplay}
                className={styles['share-settings-table']}
            >
                <Column
                    title={i18n.t(`${nameSpace}.usersWithAccess`)}
                    dataIndex="email"
                    key="email"
                    ellipsis
                    render={(cellValue, row: SharedUserToDisplay) => {
                        const recipientPhone = usersPhonesMap.get(cellValue);
                        return (
                            <RecipientCell
                                cellValue={cellValue}
                                recipientForPhoneEditing={recipientForPhoneEditing}
                                setRecipientForPhoneEditing={setRecipientForPhoneEditing}
                                setPhone={setPhone}
                                phoneInfo={recipientPhone?.phonesVector?.currentPhone}
                                fileOwnerId={fileSelected.owner_id}
                                sharedUser={row}
                                showPhone={policyRequirements.requiresPhone}
                                showPassword={policyRequirements.requiresPassword}
                            />
                        );
                    }}
                />
                <Column
                    title={i18n.t(`${nameSpace}.role`)}
                    dataIndex="recipientRole"
                    width="120px"
                    render={(cellValue, row: SharedUserToDisplay) => (
                        <>
                            {!row.deleted && checkIsElementEnabled(row.userId) && (
                                <RecipientRoleSelect
                                    value={cellValue}
                                    sharedItemType={sharedItemType}
                                    onChange={(newValue) => changeRole(row.email, newValue)}
                                />
                            )}
                        </>
                    )}
                    key="readOnly"
                />
                <Column
                    title=""
                    dataIndex=""
                    width="100px"
                    key="delete"
                    render={(cellValue, row: SharedUser) => {
                        const isDeleted = row.deleted;

                        return (
                            <Button
                                disabled={!checkIsElementEnabled(row.userId)}
                                type="link"
                                className="user-6access-button"
                                data-testid="manageAccess_revokeFirstUser"
                                onClick={() => handleRevokeClick(row.email, isDeleted, row.recipientType)}
                            >
                                {i18n.t(`${nameSpace}.${isDeleted ? 'share' : 'revoke'}`)}
                            </Button>
                        );
                    }}
                />
            </Table>

            <RevocationWarningModal
                isOpen={warningModalState.isOpen}
                userEmail={warningModalState.userEmail}
                isGroup={warningModalState.recipientType === 'group'}
                isForwardingEnabled={forwardingStatus.isForwardingEnabled}
                isDomainForwardingEnabled={forwardingStatus.isDomainForwardingEnabled}
                onContinue={handleWarningContinue}
                onChangePolicy={handleWarningChangePolicy}
                onRevokeAllDomain={handleRevokeAllDomain}
                onCancel={handleWarningCancel}
            />
        </>
    );
});

export default ShareSettingsTable;
